<template>
    <transition name="fade">
        <client-only>
            <BaseButton
                v-if="!isAuthenticated"
                element="a"
                target="_self"
                @click.prevent="login"
            >
                Log in to enroll
            </BaseButton>
            <p v-else-if="!hasValidMembership">
                <strong>E-course enrollment requires {{ requiredMembershipCode }} membership.</strong>
            </p>
            <p v-else-if="doceboLoading">
                Please wait while we enroll you to the e-course. You will be redirected momentarily....
            </p>
            <p
                v-else-if="doceboError"
                class="error"
            >
                {{ doceboError.message }}. Please <a href="">reload the page</a> and try again.
            </p>
            <BaseButton
                v-else
                @click.prevent="handleDoceboButton"
            >
                Go to course <BaseIcon icon="arrow-up-right" />
            </BaseButton>
        </client-only>
    </transition>
</template>

<script setup>
import axios from 'axios';
import Bugsnag from '@bugsnag/js';

const props = defineProps({
    id: {
        type: [Number, String],
        required: true,
        default: null
    }
});

const doceboLoading = ref(false);
const doceboError = ref(null);
const doceboUser = ref(null);

const route = useRoute();
const {data: session, signIn, status} = useAuth();
const {isAuthenticated, hasValidMembership, requiredMembershipCode} = useMembershipGate();

const login = async() => await signIn(
    'uroweb',
    {redirectTo: route.fullPath}
);

const { public: {apiBaseURL: baseURL}} = useRuntimeConfig() ?? {};

const cmsAPI = () => axios.create({baseURL});

/**
 * If the user is logged in,
 *      create a docebo account,
 *      enroll them on the course,
 *      redirect them to docebo.
 */
const handleDoceboButton = async() => {
    const user = toValue(session)?.user;
    // Ensure an e-course ID is present
    if (props?.id && user?.email) {
        doceboLoading.value = true;

        try {
            const isUserAuthenticated = toValue(status) === 'authenticated';
            if (!isUserAuthenticated) {
                throw new Error('Failed to authenticate');
            }
            console.log('User is authenticated');

            await findOrCreateDoceboUser(
                user.email,  // 'renske@digitalnatives.nl', // Debug
                user.lastName,
                user.firstName,
                user.id // '208128' // Debug
            );

            await enrollUserToCourse(props.id);
            await getCourseSsoLink(props.id);
            doceboLoading.value = false;
        } catch (e) {
            handleDoceboError(e);
            doceboLoading.value = false;
        }
    } else {
        console.warn('Deocebo did not Execute', props?.id && user?.email, user, props.id);
        handleDoceboError(new Error(
            user?.email ? 'Failed to enroll to the e-course.' : 'User is not logged in.'
        ));
    }
};

const findOrCreateDoceboUser = async(email, firstName, lastName, id) => {
    const findPath = 'docebo/user/findOrCreate';
    const body = {email, firstName, lastName, id};
    const {data} = await cmsAPI().post(findPath, body);

    const setupUser = toValue(session)?.user ? {...toValue(session)?.user} : {};
    setupUser.doceboUsername = data.username;
    setupUser.doceboId = data.id;

    if (!data.success) {
        throw new Error('Failed to create docebo user');
    }

    doceboUser.value = setupUser;
};

const enrollUserToCourse = async(courseId) => {
    validateDoceboUser();

    const enrollPath = 'docebo/course/enroll';
    const body = {
        courseId,
        userId: doceboUser.value.doceboId,
        username: doceboUser.value.doceboUsername
    };
    const res = await cmsAPI().post(enrollPath, body);
    const {data} = res;

    if (!data.success) {
        throw new Error('Failed to enroll to the e-course.');
    }
};

const getCourseSsoLink = async(courseId) => {
    validateDoceboUser();

    const ssoPath = 'docebo/user/ssoLink';
    const body = {
        username: doceboUser.value.doceboUsername,
        courseId
    };
    const {data} = await cmsAPI().post(ssoPath, body);

    if (data.success && data.link) {
        window.open(data.link);
    } else {
        throw new Error('Failed to generate a link to the e-course.');
    }
};

const validateDoceboUser = () => {
    if (!doceboUser || !doceboUser.value || !doceboUser.value.doceboUsername || !doceboUser.value.doceboId) {
        throw new Error('Failed to enroll to the e-course.');
    }
};

const handleDoceboError = (error) => {
    Bugsnag.notify(error);
    doceboError.value = error;
};

</script>
